<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useLogsStore } from '@/stores/logs'
import UTALogsMap from '@/components/UTALogsMap.vue'
import Modal from '@/components/UTAModal.vue'
import calcDatas from '@/helpers/calcDatas'

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const translaterStore   = useTranslaterStore()
const logsStore         = useLogsStore()

const getTranslate  = computed(() => translaterStore.getTranslate)
const logs          = computed(() => logsStore.logs)
const logsProcess   = computed(() => logsStore.logsProcess)
const logsMsg       = computed(() => logsStore.logsMsg)

const inputSearch       = ref('')
const pageNumber        = ref(1)
const logsMapCoords     = ref([])
const showLogsMap       = ref(false)
const logsMode          = ref('device')
const UTAGNSSLogsFrom   = ref(calcDatas('tBegin'))
const UTAGNSSLogsTill   = ref(calcDatas('tEnd'))
const eventName         = ref('')
const eventDate         = ref(0)

function changePage(cursor, dir) {

    let params = {
        'uuid_car_array': props.uuid,
        'c.limit': 20, 
        'c.direction': dir, 
        'c.cursor': cursor,
        'data': logsMode.value,
        'start': Math.round(new Date(UTAGNSSLogsFrom.value).getTime() / 1000),
        'stop': Math.round(new Date(UTAGNSSLogsTill.value).getTime() / 1000),
        'c.order': 'DESC'
    }

    if(inputSearch.value) 
        params['c.q'] = inputSearch.value

    logsStore.readLogs(params).then(res => {
        if(res.array) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

function submitForm() {

    let data = {
        data: logsMode.value, 
        'c.limit': 20,
        'c.order': 'DESC',
        uuid_car_array: props.uuid
    }

    if(UTAGNSSLogsFrom.value && UTAGNSSLogsTill.value) {
        data.start  = Math.round(new Date(UTAGNSSLogsFrom.value).getTime() / 1000)
        data.stop   = Math.round(new Date(UTAGNSSLogsTill.value).getTime() / 1000)
    }

    pageNumber.value = 1
    logsStore.readLogs(data)
}

function extraParamsArray(extraParam) {

    const obj = JSON.parse(extraParam)
    let arr = []

    Object.keys(obj).map(item => {
        if(item !== 'GEO') arr.push({ name: item, value: obj[item] })
    })

    return arr
}

onBeforeUnmount(() => {

    logsStore.clearLogs()
})

watch(inputSearch, input => {

    input.length > 2 ? 
        logsStore.readLogs({ 'c.limit': 20, 'c.q': input }) :
        logsStore.readLogs({ 'c.limit': 20 })
})
</script>

<template>

    <div class="uta-logs">

        <form action="/" id="logsForm" @submit.prevent="submitForm()">

            <div class="row">
                <div class="col-12 col-sm-6 col-xxl-4">
                    <div class="mb-4 form-group">
                        <label for="UTAGNSSLogsFrom">{{ getTranslate.HISTORY_START }}:</label>
                        <input 
                            class="uta-input uta-input--block" 
                            type="datetime-local" 
                            id="UTAGNSSLogsFrom"
                            v-model="UTAGNSSLogsFrom"
                        >
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-xxl-4">
                    <div class="mb-4 form-group">
                        <label for="UTAGNSSLogsTill">{{ getTranslate.HISTORY_END }}:</label>
                        <input 
                            class="uta-input uta-input--block" 
                            type="datetime-local" 
                            id="UTAGNSSLogsTill"
                            v-model="UTAGNSSLogsTill"
                        >
                    </div>
                </div>
            </div>

            <button class="uta-btn uta-btn--main" type="submit">
                <template v-if="!logsProcess">
                    {{ getTranslate.BTN_REQUEST }}
                </template>
                <template v-else>
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </template>
            </button>

            <div 
                v-if="logsMsg && !logsMsg.success"
                class="mt-4 uta-alert uta-alert--danger"
            >
                <span class="material-icons uta-alert__icon">error_outline</span>
                <span class="uta-alert__text">
                    {{ getTranslate[logsMsg.text] || logsMsg.text }}
                </span>
            </div>

            <template v-if="logs.array?.length">

                <div class="accordion my-4" id="logsAccordion" v-if="logs.array.length">

                    <div class="accordion-item uta-logs__log" v-for="(log, index) in logs.array" :key="index">

                        <div 
                            class="collapsed uta-logs__log-header" 
                            :id="'logsAccordion_collapse_' + index" 
                            data-bs-toggle="collapse" 
                            :data-bs-target="'#collapse' + index" 
                            aria-expanded="false" 
                            :aria-controls="'collapse' + index"
                        >
                            <div class="container-fluid">
                                <div class="row">
                                    <div class="col-12 col-md-6 mb-4 mb-md-0">
                                        <div class="uta-logs__log-header-name">
                                            {{ log.message }}
                                        </div>
                                    </div>
                                    <div class="col-12 col-md-6 text-md-end d-flex justify-content-end align-items-center">
                                        <div class="uta-logs__log-header-date">
                                            {{ new Date(Number(log.createdAt / 1000)).toLocaleString() }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <span class="material-icons">
                                expand_less
                            </span>

                        </div>

                        <div 
                            :id="'collapse' + index" 
                            class="uta-logs__log-body collapse" 
                            :aria-labelledby="'logsAccordion_collapse_' + index" 
                            data-bs-parent="#logsAccordion"
                        >

                            <div class="uta-list">

                                <div class="uta-list__item uta-list__item--first">
                                    <div class="row">
                                        <div class="col-12 col-md-3 uta-list__itemTitle uta-logs__log-title">
                                            Details: 
                                        </div>
                                        <div class="col-12 col-md-9 uta-list__itemValue uta-logs__log-value">
                                            <pre>{{ log.details }}</pre>
                                        </div>
                                    </div>
                                </div>

                                <div class="uta-list__item">
                                    <div class="row">
                                        <div class="col-12 col-md-3 uta-list__itemTitle uta-logs__log-title">
                                            Message: 
                                        </div>
                                        <div class="col-12 col-md-9 uta-list__itemValue uta-logs__log-value">
                                            {{ log.message }}
                                        </div>
                                    </div>
                                </div>

                                <div class="uta-list__item">
                                    <div class="row">
                                        <div class="col-12 col-md-3 uta-list__itemTitle uta-logs__log-title">
                                            Action:
                                        </div>
                                        <div class="col-12 col-md-9 uta-list__itemValue uta-logs__log-value">
                                            {{ log.action }}
                                        </div>
                                    </div>
                                </div>

                                <div class="uta-list__item">
                                    <div class="row">
                                        <div class="col-12 col-md-3 uta-list__itemTitle uta-logs__log-title">
                                            Level: 
                                        </div>
                                        <div class="col-12 col-md-9 uta-list__itemValue uta-logs__log-value">
                                            {{ log.level }}
                                        </div>
                                    </div>
                                </div>

                                <template v-if="log.extraParam">

                                    <div class="uta-list__item">
                                        <div class="row" v-for="(item, index) in extraParamsArray(log.extraParam)" :key="index">
                                            <div class="col-12 col-md-3 uta-list__itemTitle uta-logs__log-title">
                                                {{ item.name }}:
                                            </div>
                                            <div class="col-12 col-md-9 uta-list__itemValue uta-logs__log-value">
                                                {{ item.value }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="uta-list__item" v-if="log.details && log.details['geo.lat'] && log.details['geo.lon']">
                                        <div class="row">
                                            <div class="col-12 col-md-3 uta-list__itemTitle uta-logs__log-title">
                                                {{ getTranslate.ECU_GNSS_LOGS_GEO }}:
                                            </div>
                                            <div class="col-12 col-md-9 uta-list__itemValue uta-logs__log-value">
                                                {{ log.details['geo.lat'] }}, {{ log.details['geo.lon'] }}
                                                <a 
                                                    href="#"
                                                    class="uta-logs__log-value-link" 
                                                    @click.prevent="
                                                        logsMapCoords = [log.details['geo.lon'], log.details['geo.lat']], 
                                                        eventName = log.message,
                                                        showLogsMap = true
                                                    "
                                                >
                                                    [ {{ getTranslate.BTN_MAP }} ] 
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                </template>

                            </div>
                        </div>

                    </div>

                </div>

                <div class="my-4 uta-users__pagination" v-if="logs.c && (logs.c.isBefore || logs.c.isAfter)">
                    <div class="row">
                        <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                            <div class="uta-pagination">
                                <button 
                                    :disabled="logsProcess || !logs.c.isBefore"
                                    class="uta-btn uta-btn--sm uta-pagination__prev" 
                                    @click.prevent="changePage(logs.c.start, 'before')"
                                >
                                    <span class="material-icons">
                                        navigate_before
                                    </span>
                                </button>
                                <div class="uta-pagination__current">
                                    {{ pageNumber }}
                                </div>
                                <button 
                                    :disabled="logsProcess || !logs.c.isAfter"
                                    class="uta-btn uta-btn--sm uta-pagination__next" 
                                    @click.prevent="changePage(logs.c.stop, 'after')"
                                >
                                    <span class="material-icons">
                                        navigate_next
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </template>

        </form>

        <transition name="bounce">
            <Modal 
                v-if="showLogsMap"
                :header="eventName"
                size="md"
                @closeModal="
                    showLogsMap = false
                "
            >
                <template v-slot:body>
                    <UTALogsMap 
                        :eventDate="Number(eventDate * 1000)"
                        :coords="logsMapCoords" 
                        @closeModal="
                            showLogsMap = false
                        "
                    />
                </template>
            </Modal>
        </transition>

    </div>

</template>

<style lang="scss">
@import '@/assets/styles/scss/components/logs.scss';
</style>